import React from "react";
import ConfirmDialog, { type ConfirmTone } from "components/ui/confirm-dialog";

/**
 * Adapter over the shared `components/ui/confirm-dialog`.
 *
 * This file was a 200-line hand-built portal dialog with its own backdrop, no
 * focus trap, no Escape handling and light-only colours — it rendered as a
 * white box in dark mode. The props are unchanged so existing call sites
 * (CampaignConfirmationDialogs and friends) keep working; only the rendering
 * moved to the shared component.
 */

type LegacyType = "warning" | "danger" | "info";

const TYPE_TO_TONE: Record<LegacyType, ConfirmTone> = {
    warning: "warning",
    danger: "danger",
    info: "info",
};

export interface SupportConfirmationDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: React.ReactNode;
    message?: React.ReactNode;
    confirmText?: string;
    cancelText?: string;
    type?: LegacyType;
    isLoading?: boolean;
    disabled?: boolean;
    showIcon?: boolean;
    children?: React.ReactNode;
    requireConfirmation?: boolean;
    confirmationText?: string;
    className?: string;
}

const ConfirmationDialog: React.FC<SupportConfirmationDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = "Confirm",
    cancelText = "Cancel",
    type = "warning",
    isLoading = false,
    disabled = false,
    showIcon = true,
    children,
    requireConfirmation = false,
    confirmationText = "",
    className = "",
}) => (
    <ConfirmDialog
        open={isOpen}
        title={title}
        description={message}
        tone={TYPE_TO_TONE[type] ?? "warning"}
        showIcon={showIcon}
        confirmLabel={confirmText}
        cancelLabel={cancelText}
        isLoading={isLoading}
        disabled={disabled}
        requireConfirmation={requireConfirmation}
        confirmationText={confirmationText}
        onConfirm={onConfirm}
        onCancel={onClose}
        className={className}
    >
        {children}
    </ConfirmDialog>
);

export default ConfirmationDialog;
